import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mail, Loader2, CheckCircle2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error(language === 'fr' ? 'Entre ton email' : 'Enter your email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email,
        origin_url: window.location.origin
      });
      setSent(true);
      toast.success(language === 'fr' ? 'Email envoyé !' : 'Email sent!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi' : 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Back button */}
        <button
          onClick={() => navigate('/login')}
          className="flex items-center gap-2 text-stone-500 hover:text-stone-700 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'fr' ? 'Retour à la connexion' : 'Back to login'}
        </button>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-stone-100">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FFE66D] flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-serif text-2xl font-semibold text-stone-900">
              {language === 'fr' ? 'Mot de passe oublié ?' : 'Forgot password?'}
            </h1>
            <p className="text-stone-500 mt-2 text-sm">
              {language === 'fr' 
                ? 'Pas de panique ! Entre ton email et on t\'envoie un lien de réinitialisation.'
                : 'No worries! Enter your email and we\'ll send you a reset link.'}
            </p>
          </div>

          {sent ? (
            /* Success state */
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-serif text-xl font-semibold text-stone-900 mb-2">
                {language === 'fr' ? 'Email envoyé !' : 'Email sent!'}
              </h2>
              <p className="text-stone-600 mb-6">
                {language === 'fr' 
                  ? `Si un compte existe avec ${email}, tu recevras un lien de réinitialisation.`
                  : `If an account exists with ${email}, you'll receive a reset link.`}
              </p>
              <p className="text-stone-400 text-sm mb-6">
                {language === 'fr' 
                  ? 'Vérifie aussi tes spams !'
                  : 'Check your spam folder too!'}
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="btn-primary w-full"
              >
                {language === 'fr' ? 'Retour à la connexion' : 'Back to login'}
              </Button>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={language === 'fr' ? 'ton@email.com' : 'your@email.com'}
                    className="pl-12 h-12 rounded-xl border-stone-200 focus:border-[#FF6B6B] focus:ring-[#FF6B6B]"
                    data-testid="forgot-password-email"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="btn-primary w-full h-12"
                disabled={loading}
                data-testid="forgot-password-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    {language === 'fr' ? 'Envoi en cours...' : 'Sending...'}
                  </>
                ) : (
                  language === 'fr' ? 'Envoyer le lien' : 'Send reset link'
                )}
              </Button>
            </form>
          )}

          {/* Footer */}
          {!sent && (
            <p className="text-center text-sm text-stone-500 mt-6">
              {language === 'fr' ? 'Tu te souviens ?' : 'Remember your password?'}{' '}
              <Link to="/login" className="text-[#FF6B6B] font-medium hover:underline">
                {language === 'fr' ? 'Se connecter' : 'Sign in'}
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
